import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import Fuse from 'fuse.js';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { DriversService } from '../../root/drivers.service';
import { SnackBarService } from '../../shared/snackbar.service';
import { DriverTransferRequest, DriverWithRegion } from '../../types';
import { fuse } from '../../utilities/fuse';
import { OwnerDashboardService } from '../owner-dashboard.service';

const driverSearchOptions: Fuse.IFuseOptions<DriverWithRegion> = {
  distance: 100,
  keys: ['driverName', 'regionName'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

@Component({
  selector: 'lohi-request-driver-transfer',
  templateUrl: './request-driver-transfer.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestDriverTransferComponent implements OnInit {
  public loading$$ = new BehaviorSubject(false);
  public requestForm = this.fb.group({
    requests: this.fb.array(
      [
        this.fb.group({
          driver: [null, [Validators.required]],
          transferDate: [null, Validators.required],
        }),
      ],
      [Validators.min(1)],
    ),
  });
  public driverSearch = new FormControl();
  public drivers$: Observable<DriverWithRegion[]>;
  public today = new Date();

  constructor(
    public dialogRef: MatDialogRef<RequestDriverTransferComponent>,
    private snackBar: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public data,
    private fb: FormBuilder,
    private driverService: DriversService,
    private ownerDashboardService: OwnerDashboardService,
  ) {}

  public ngOnInit(): void {
    this.drivers$ = combineLatest([
      this.driverService.driversWithRegion$.pipe(
        filter((drivers) => !!drivers),
        map(fuse(driverSearchOptions)),
      ),
      this.driverSearch.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, search]) => {
        if (!search) {
          return fused.data;
        }
        return fused.fuse.search(search).map((value) => value.item);
      }),
    );
  }

  public sendRequest() {
    if (this.requestForm.valid) {
      this.loading$$.next(true);
      const requestBody: DriverTransferRequest[] = this.requestsArray.value.map((item) => {
        return {
          currentRegionId: item.driver.regionId,
          driverId: item.driver.driverId,
          toRegionId: this.data.regionId,
          transferDate: item.transferDate,
        } as DriverTransferRequest;
      });
      this.ownerDashboardService.createDriverTransferRequests$(requestBody).subscribe(
        (resp) => {
          this.loading$$.next(false);
          this.snackBar.showMessage('Transfer requests submitted successfully with us.');
          this.closeDialog();
        },
        (error) => {
          this.loading$$.next(false);
          this.snackBar.showError(
            'Oops! This request was not sent. Please contact support if you wish to transfer this driver.',
          );
        },
      );
    } else {
      this.snackBar.showError('Form is not valid. Please check and try again.');
    }
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public removeRequest(i: number) {
    this.requestsArray.removeAt(i);
  }

  public get requestsArray(): FormArray {
    return this.requestForm.get('requests') as FormArray;
  }

  public addNewRequest() {
    this.requestsArray.push(
      this.fb.group({
        driver: [null, [Validators.required]],
        transferDate: [null, Validators.required],
      }),
    );
  }
}
